import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

const FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// Widget de pergunta estruturada (estilo AskUserQuestion do Claude Code):
// renderiza o input certo por tipo (text / choice / multi) e devolve a resposta
// via onSubmit (string para text/choice, array para multi). Usado pelo copiloto
// (CopilotPanel) e pelo assistente de chat.
// REGRA: choice/multi SEMPRE oferecem também um campo de TEXTO LIVRE ("Outro…"),
// para o usuário responder fora das opções sugeridas.
const useAskStyles = makeStyles(theme => ({
  box: { alignSelf: "stretch", background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 12, padding: 12, boxShadow: "0 1px 2px rgba(15,22,30,.05)" },
  q: { fontSize: 13.5, fontWeight: 600, color: theme.app.ink, marginBottom: 10, lineHeight: 1.45 },
  opts: { display: "flex", flexDirection: "column", gap: 7 },
  opt: { display: "flex", alignItems: "center", gap: 9, textAlign: "left", width: "100%", border: `1px solid ${theme.app.border}`, background: "#fff", borderRadius: 9, padding: "9px 11px", fontSize: 13, color: theme.app.ink, cursor: "pointer", fontFamily: FONT, transition: "border-color .12s, background .12s", "&:hover": { borderColor: "#2C353D", background: theme.app.hover } },
  optSel: { borderColor: "#2C353D", background: "#F4FFCC" },
  tick: { width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${theme.app.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, lineHeight: 1, color: "#2C353D", fontWeight: 700 },
  tickSel: { borderColor: "#2C353D", background: "#CEFF00" },
  input: { width: "100%", boxSizing: "border-box", border: `1px solid ${theme.app.border}`, borderRadius: 9, padding: "9px 11px", fontSize: 13, fontFamily: FONT, outline: "none", "&:focus": { borderColor: "#2C353D" } },
  // linha de texto livre (em choice/multi) — sempre disponível
  freeRow: { display: "flex", alignItems: "center", gap: 7, marginTop: 7 },
  freeInput: { flex: 1, boxSizing: "border-box", border: `1px solid ${theme.app.border}`, borderRadius: 9, padding: "9px 11px", fontSize: 13, fontFamily: FONT, outline: "none", "&:focus": { borderColor: "#2C353D" } },
  freeSend: { flexShrink: 0, width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", background: "#2C353D", color: "#fff", border: "none", borderRadius: 8, fontSize: 16, cursor: "pointer", "&:disabled": { background: theme.app.border, color: theme.app.textTertiary, cursor: "default" } },
  actions: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 },
  send: { background: "#2C353D", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT, "&:disabled": { background: theme.app.border, color: theme.app.textTertiary, cursor: "default" } },
}));

const AskWidget = ({ spec, onSubmit }) => {
  const c = useAskStyles();
  const [text, setText] = useState("");
  const [sel, setSel] = useState([]);
  const type = spec.input_type;
  const options = Array.isArray(spec.options) ? spec.options.filter(Boolean) : [];

  // CHOICE: opções clicáveis (envia na hora) + campo de texto livre
  if (type === "choice" && options.length) {
    const submitText = () => {
      const v = text.trim();
      if (v) onSubmit(v);
    };
    return (
      <div className={c.box}>
        <div className={c.q}>{spec.question}</div>
        <div className={c.opts}>
          {options.map((o, i) => (
            <button key={i} type="button" className={c.opt} onClick={() => onSubmit(o)}>
              <span className={c.tick} /> {o}
            </button>
          ))}
        </div>
        <div className={c.freeRow}>
          <input
            className={c.freeInput}
            placeholder="Outro… (escreva sua resposta)"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitText();
              }
            }}
          />
          <button type="button" className={c.freeSend} disabled={!text.trim()} onClick={submitText} title="Enviar">↑</button>
        </div>
      </div>
    );
  }

  // MULTI: checkboxes + campo de texto livre; Confirmar envia selecionados + livre
  if (type === "multi" && options.length) {
    const toggle = o => setSel(s => (s.includes(o) ? s.filter(x => x !== o) : [...s, o]));
    const confirm = () => {
      const extra = text.trim();
      const out = [...sel, ...(extra ? [extra] : [])];
      if (out.length) onSubmit(out);
    };
    return (
      <div className={c.box}>
        <div className={c.q}>{spec.question}</div>
        <div className={c.opts}>
          {options.map((o, i) => {
            const on = sel.includes(o);
            return (
              <button key={i} type="button" className={`${c.opt} ${on ? c.optSel : ""}`} onClick={() => toggle(o)}>
                <span className={`${c.tick} ${on ? c.tickSel : ""}`}>{on ? "✓" : ""}</span> {o}
              </button>
            );
          })}
        </div>
        <div className={c.freeRow}>
          <input
            className={c.freeInput}
            placeholder="Outro… (escreva e inclua na resposta)"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                confirm();
              }
            }}
          />
        </div>
        <div className={c.actions}>
          <button type="button" className={c.send} disabled={!sel.length && !text.trim()} onClick={confirm}>Confirmar</button>
        </div>
      </div>
    );
  }

  // TEXT (e fallback se vier choice/multi sem opções)
  const submit = () => {
    const v = text.trim();
    if (v) onSubmit(v);
  };
  return (
    <div className={c.box}>
      <div className={c.q}>{spec.question}</div>
      <input
        className={c.input}
        autoFocus
        placeholder={spec.placeholder || "Sua resposta…"}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
      />
      <div className={c.actions}>
        <button type="button" className={c.send} disabled={!text.trim()} onClick={submit}>Enviar</button>
      </div>
    </div>
  );
};

export default AskWidget;
